import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { tourStateKeys } from '@/features/tours';
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/shared/ui/card";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useToast } from "@/shared/hooks/use-toast";
import BaseLayout from '@/shared/components/accounts/AccountBase';
import { login } from '@/shared/api/authentication-api';
import { LoginPayload } from '@/types/authentication';
import { handleApiError } from '@/shared/api/requests';
import { useUser } from '@/shared/hooks/useUser';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginError {
  [key: string]: string[];
}

const LoginPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, loading } = useUser();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<LoginError>({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/app', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: []
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setPasswordExpired(false);

    try {
      const loginPayload: LoginPayload = {
        email: formData.email,
        password: formData.password,
      };

      await login(loginPayload);

      // Drop any tour state cached before this session existed (a pre-auth
      // fetch, or the previous user's states — logout never reloads the
      // page). TourProvider refetches under the new session immediately.
      await queryClient.resetQueries({ queryKey: tourStateKeys.all() });

      // Success toast
      toast({
        title: "Success",
        description: "Login successful! Redirecting...",
        variant: 'default',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });

      // Redirect to app dashboard - DashboardRedirect component will handle the rest
      navigate('/app');

    } catch (error: any) {
      // Check if password has expired
      if (error?.data?.password_expired) {
        setPasswordExpired(true);
        setExpiredMessage(
          error.data.message || 
          'Your password has expired. A password reset link has been sent to your email.'
        );
        return;
      }

      const errorMessage = handleApiError(error);
      
      // Handle specific error formats
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      } else if (error?.data?.non_field_errors) {
        setErrors({ non_field_errors: error.data.non_field_errors });
      } else {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
          className: 'bg-red-500 text-white',
          duration: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <BaseLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LogoLoading 
            variant="breathing" 
            size="xl" 
            message="Checking authentication..." 
          />
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Log in to your account</CardTitle>
        </CardHeader>
        <CardContent>
          {passwordExpired ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-amber-900 mb-2">
                      Password Reset Required
                    </h3>
                    <p className="text-amber-800 mb-3">
                      {expiredMessage}
                    </p>
                    <div className="bg-white border border-amber-200 rounded-md p-4 mt-4">
                      <p className="text-sm font-semibold text-amber-900 mb-2">
                        📧 Check Your Email
                      </p>
                      <p className="text-sm text-amber-800">
                        We've sent a password reset link to <strong>{formData.email}</strong>. 
                        Click the link in the email to create a new password.
                      </p>
                    </div>
                    <div className="mt-4 text-sm text-amber-700">
                      <p className="font-medium mb-1">Didn't receive the email?</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Check your spam or junk folder</li>
                        <li>Make sure you entered the correct email address</li>
                        <li>Wait a few minutes and try logging in again</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => {
                  setPasswordExpired(false);
                  setFormData({ email: '', password: '' });
                }}
                variant="outline"
                className="w-full"
              >
                ← Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.non_field_errors && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {errors.non_field_errors.join(' ')}
                  </AlertDescription>
                </Alert>
              )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? "border-error-500" : ""}
              />
              {errors.email && (
                <p className="text-sm text-error-500">{errors.email.join(' ')}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className={errors.password ? "border-error-500" : ""}
              />
              {errors.password && (
                <p className="text-sm text-error-500">{errors.password.join(' ')}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </Button>
          </form>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <a 
            href="/reset-password"
            className="text-sm text-primary-500 hover:text-primary-600 text-center w-full"
          >
            Forgot your password?
          </a>
          <div className="text-sm text-center">
            Don't have an account?{' '}
            <a href="/signup" className="text-primary-500 hover:text-primary-600">
              Sign up
            </a>
          </div>
        </CardFooter>
      </Card>
    </BaseLayout>
  );
};

export default LoginPage;